import { useState } from 'react';
import { 
  ExternalLink, ShoppingCart, 
  Search, 
  Code, Activity, FlaskConical, CheckCircle2,
  Layers
} from 'lucide-react';

const recipes = [
  {
    id: "env-monitor-01",
    title: "EcoScout: WiFi Environmental Monitor",
    description: "High-precision temp/humidity tracking with OLED local display and n8n webhook reporting.",
    difficulty: "Beginner",
    category: "Home Automation",
    price: "Free",
    image: "https://images.unsplash.com/photo-1555664424-778a1e5e1b48?auto=format&fit=crop&q=80&w=800",
    stats: { components: 4, setup_time: "15 min", reliability: "99.9%" },
    bom: [
      { name: "ESP32 DevKit V1", qty: 1, source: "https://diymechstore.com/products/esp32-devkit" },
      { name: "DHT22 Sensor", qty: 1, source: "https://diymechstore.com/products/dht22" },
      { name: "0.96\" OLED (I2C)", qty: 1, source: "https://diymechstore.com/products/oled-096" },
      { name: "Jumper Wire Pack", qty: 1, source: "https://diymechstore.com/products/jumper-wires" }
    ],
    n8n_flow: { nodes: 3, action: "Post to Slack/Sheets" },
    tags: ["ESP32", "WiFi", "I2C", "n8n"]
  },
  {
    id: "lora-gate-02",
    title: "FieldCommander: LoRa Gateway",
    description: "Long-range sensor hub for field operations. Bridges 433/915MHz signals to MQTT.",
    difficulty: "Intermediate",
    category: "Communications",
    price: "$9.99 (PRO)",
    image: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80&w=800",
    stats: { components: 6, setup_time: "45 min", reliability: "98.5%" },
    bom: [
      { name: "Adafruit Feather M0 LoRa", qty: 1, source: "https://diymechstore.com/products/feather-m0" },
      { name: "High-Gain Antenna", qty: 1, source: "https://diymechstore.com/products/lora-antenna" },
      { name: "IP67 Enclosure", qty: 1, source: "https://diymechstore.com/products/ip67-case" }
    ],
    n8n_flow: { nodes: 5, action: "MQTT to Database Bridge" },
    tags: ["LoRa", "M0", "Telemetry", "Rugged"]
  },
  {
    id: "solar-bnc-03",
    title: "SunThief: MPPT Solar Balancer",
    description: "Intelligent LiFePO4 charging logic with real-time MOSFET thermal monitoring.",
    difficulty: "Advanced",
    category: "Power Systems",
    price: "$14.99 (PRO)",
    image: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&q=80&w=800",
    stats: { components: 12, setup_time: "120 min", reliability: "95.0%" },
    bom: [
      { name: "STM32 BluePill", qty: 1, source: "https://diymechstore.com/products/bluepill" },
      { name: "IRFZ44N MOSFET", qty: 4, source: "https://diymechstore.com/products/mosfet-irfz" },
      { name: "100uH Power Inductor", qty: 2, source: "https://diymechstore.com/products/inductor-100u" }
    ],
    n8n_flow: { nodes: 2, action: "Battery Health Alert" },
    tags: ["Solar", "MPPT", "MOSFET", "LiFePO4"]
  }
];

export default function Recipes() {
  const [search, setSearch] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);

  const filtered = recipes.filter(r => 
    r.title.toLowerCase().includes(search.toLowerCase()) || 
    r.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ minHeight: '100vh', background: '#050912', color: 'var(--text-primary)' }}>
      {/* Header Area */}
      <div style={{ 
        padding: '3rem 2rem', 
        background: 'linear-gradient(to bottom, rgba(0,255,136,0.05), transparent)',
        borderBottom: '1px solid var(--border-color)',
        textAlign: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <FlaskConical size={32} color="var(--color-primary)" />
          <h1 style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Known Good <span style={{ color: 'var(--color-primary)' }}>Recipes</span></h1>
        </div>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 2rem' }}>
          Curated, field-tested electronics blueprints. 1-Click procurement for code, wiring, and components.
        </p>

        <div style={{ maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input 
            type="text"
            placeholder="Search by tech, category, or part..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ 
              width: '100%', padding: '0.875rem 1rem 0.875rem 3rem', background: 'var(--bg-surface)', 
              border: '1px solid var(--border-color)', borderRadius: '12px', color: 'var(--text-primary)',
              fontSize: '1rem', outline: 'none', transition: 'all 0.2s'
            }}
            onFocus={e => e.currentTarget.style.borderColor = 'var(--color-primary)'}
            onBlur={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          />
        </div>
      </div>

      <div className="page-container" style={{ padding: '3rem 1.5rem', display: 'grid', gridTemplateColumns: selectedRecipe ? '1fr 400px' : '1fr', gap: '2rem' }}>
        
        {/* Recipe Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
          {filtered.map(recipe => (
            <div 
              key={recipe.id}
              onClick={() => setSelectedRecipe(recipe)}
              style={{ 
                background: 'var(--bg-surface)', border: selectedRecipe?.id === recipe.id ? '2px solid var(--color-primary)' : '1px solid var(--border-color)',
                borderRadius: '16px', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.3s ease',
                transform: selectedRecipe?.id === recipe.id ? 'translateY(-4px)' : 'none',
                boxShadow: selectedRecipe?.id === recipe.id ? '0 10px 30px rgba(0,255,136,0.15)' : 'none'
              }}
            >
              <div style={{ height: '180px', background: `url(${recipe.image}) center/cover`, position: 'relative' }}>
                <div style={{ 
                  position: 'absolute', top: '1rem', right: '1rem', 
                  background: 'rgba(5,9,18,0.85)', padding: '0.4rem 0.8rem', borderRadius: '6px',
                  fontSize: '0.75rem', fontWeight: 700, border: '1px solid var(--border-color)'
                }}>
                  {recipe.price}
                </div>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--color-primary)', fontWeight: 800, textTransform: 'uppercase', marginBottom: '0.5rem' }}>{recipe.category}</div>
                <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.75rem' }}>{recipe.title}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '1.5rem', height: '3.2rem', overflow: 'hidden' }}>
                  {recipe.description}
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {recipe.tags.map(t => (
                    <span key={t} style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.05)', padding: '0.2rem 0.5rem', borderRadius: '4px', color: 'var(--text-muted)' }}>#{t}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Selected Details Sidebar */}
        {selectedRecipe && (
          <div style={{ 
            background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-color)', padding: '2rem',
            position: 'sticky', top: '100px', height: 'calc(100vh - 120px)', overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.5rem' }}>Recipe Blueprint</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              
              {/* Core Specs */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ background: '#050912', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Difficulty</div>
                  <div style={{ fontWeight: 700 }}>{selectedRecipe.difficulty}</div>
                </div>
                <div style={{ background: '#050912', padding: '1rem', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Setup Time</div>
                  <div style={{ fontWeight: 700 }}>{selectedRecipe.stats.setup_time}</div>
                </div>
              </div>

              {/* BOM / Parts */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <ShoppingCart size={18} color="var(--color-secondary)" />
                  <h4 style={{ fontWeight: 700 }}>Bill of Materials</h4>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {selectedRecipe.bom.map((item: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.85rem' }}>{item.qty}x {item.name}</div>
                      <a href={item.source} target="_blank" rel="noreferrer" style={{ color: 'var(--color-secondary)' }}>
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  ))}
                </div>
                <button style={{ 
                  width: '100%', marginTop: '1rem', padding: '0.75rem', background: 'var(--color-secondary)', color: '#000',
                  border: 'none', borderRadius: '8px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem'
                }}>
                  <ShoppingCart size={16} /> Add All for $42.50
                </button>
              </div>

              {/* Assets */}
              <div>
                <h4 style={{ fontWeight: 700, marginBottom: '1rem' }}>Included Assets</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'rgba(0,255,136,0.05)', borderRadius: '8px', color: 'var(--color-primary)' }}>
                      <Code size={16} /> <span style={{ fontSize: '0.85rem' }}>Optimized Circuit Logic (.ino)</span> <CheckCircle2 size={14} style={{ marginLeft: 'auto' }} />
                   </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'rgba(0,207,255,0.05)', borderRadius: '8px', color: 'var(--color-secondary)' }}>
                      <Layers size={16} /> <span style={{ fontSize: '0.85rem' }}>Interactive Wiring Map</span> <CheckCircle2 size={14} style={{ marginLeft: 'auto' }} />
                   </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', background: 'rgba(168,85,247,0.05)', borderRadius: '8px', color: 'var(--color-purple)' }}>
                      <Activity size={16} /> <span style={{ fontSize: '0.85rem' }}>n8n Logic-Bridge JSON</span> <CheckCircle2 size={14} style={{ marginLeft: 'auto' }} />
                   </div>
                </div>
                <button 
                  onClick={() => alert(`Purchasing ${selectedRecipe.title} assets...`)}
                  style={{ 
                    width: '100%', marginTop: '1.5rem', padding: '1rem', background: 'var(--text-primary)', color: '#050912',
                    border: 'none', borderRadius: '8px', fontWeight: 800, cursor: 'pointer'
                  }}
                >
                  DEEP DOWNLOAD BLUEPRINT
                </button>
              </div>

            </div>
          </div>
        )}
      </div>

      <style>{`
        .page-container {
          max-width: 1400px;
          margin: 0 auto;
        }
      `}</style>
    </div>
  );
}
